import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Grid,
  LinearProgress,
  Button,
  Typography,
  Box, IconButton
} from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CheckIcon from '@mui/icons-material/Check';

import {
  apiGetPlayer,
  PlaylistState
} from '../../store/playlistSlice';
import { ReactComponent as PencilIcon } from '../../icons/pencil.svg';
import { OrganisationContext } from '../../App';
import { useNavigate, useParams } from 'react-router-dom';
import { RootState } from '../../index';
import axios from 'axios';
import { getApiBaseUrl, getAssetsBaseUrl } from '../../utils/env';
import Table from '@mui/material/Table';
import { TableCellBody, TableCellHead, TableRow } from '../common/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import removeButton from '../../icons/remove.png';
import { PlayerState } from '../types/playlist';
import ToolBarPlaylist from '../ToolBarPlaylist';
import { PlaylistCreativeDialog } from '../modal/PlaylistCreativeDialog';
import CodeIcon from '@mui/icons-material/Code';
import { Heading } from '../common/Heading';
import CloseIcon from '@mui/icons-material/Close';

export const initialPlayerState: PlayerState = {
  ad_unit: '',
  click_thru_on_tray_only: false,
  click_thru_url: '',
  enabled_tray: true,
  lumen_id: '',
  end_slate_image_filename: '',
  id: undefined,
  impression_url: '',
  impression_url2: '',
  name: '',
  player_id: 'pixels-no-ads',
  playlist_uuid: '',
  tray_image_filename: '',
  video_size: 1,
  tray_colour: '',
  type: 'editorial',
  impression_trackers: [{ url: '', tag: 'img', event: 'loadstart' }],
  lumen_trackers: [{ type: 'video', mid: '' }]
};

export default function PlaylistCreative() {
  const { playlist } = useParams();
  const navigate = useNavigate();
  const dispatchRedux = useDispatch();
  const { selectedOrganisation } = useContext(OrganisationContext);

  const [creative, setCreative] = useState<PlayerState | null>(null);

  const {
    busy,
    currentPlaylist,
    players,
  } = useSelector<RootState, PlaylistState>((state) => state.playlist);

  const onRemove = async (id: number) => {
    await axios
      .delete(`${getApiBaseUrl()}/playlist/${selectedOrganisation}/${playlist}/player/${id}`);
    dispatchRedux(apiGetPlayer({
      publisher: selectedOrganisation,
      playlistId: playlist
    }));
  };

  useEffect(() => {
    if (!selectedOrganisation) return;
    dispatchRedux(apiGetPlayer({
      publisher: selectedOrganisation,
      playlistId: playlist
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrganisation, playlist]);

  if (!currentPlaylist) {
    return <p>Loading..</p>;
  }

  return (
    <Box>
      <ToolBarPlaylist playlist={currentPlaylist}/>
      {currentPlaylist.format === 'vertical' && <Grid container alignItems={'center'} spacing={2}>
        <Grid item xs={12}>
          <Grid container sx={{
            mb: 2
          }} justifyContent={'space-between'}>
            <Grid item>
              <Heading>Creatives</Heading>
            </Grid>
            <Grid item>

              <Button color="primary" variant="contained" onClick={() => setCreative(initialPlayerState)}
                      startIcon={<AddCircleIcon/>}
                      type={'button'}>Add creative</Button>
            </Grid>
          </Grid>
          {players.length > 0 && currentPlaylist && <Table size="small">
            <TableHead>
              <TableRow>
                <TableCellHead>NAME</TableCellHead>
                <TableCellHead>CREATIVES</TableCellHead>
                <TableCellHead>TRACKERS</TableCellHead>
                <TableCellHead>TAGS</TableCellHead>
                <TableCellHead>ACTION</TableCellHead>
              </TableRow>
            </TableHead>
            <TableBody>
              {players.map((row) => {
                return (<TableRow key={row.id}>
                  {/* eslint-disable-next-line react/jsx-no-target-blank */}
                  <TableCellBody>{row.name}</TableCellBody>
                  <TableCellBody>
                    <Grid container direction="column">
                      {row.end_slate_image_filename && (
                        <Grid container item xs={12} alignItems="center">
                          <Grid item>
                            <img
                              src={`${getAssetsBaseUrl()}/end-slates/${row.end_slate_image_filename}`}
                              alt="End slate"
                              style={{ width: '100px', height: 'auto', verticalAlign: 'bottom' }}
                            />
                          </Grid>
                        </Grid>
                      )}
                      {row.tray_image_filename && (
                        <Grid container item xs={12} alignItems="center">
                          <Grid item>
                          <img
                              src={`${getAssetsBaseUrl()}/short-tray/${row.tray_image_filename}`}
                              alt="Thumbnail"
                              style={{ maxWidth: 100, height: 'auto', maxHeight: 100 }}
                            />
                          </Grid>
                        </Grid>
                      )}
                    </Grid>
                  </TableCellBody>
                  <TableCellBody>
                    <Grid container alignItems="center" spacing={1}>
                      <Grid container item xs={12} alignItems="center">
                        <Grid item>
                          {Boolean(row.click_thru_url) && <CheckIcon color="success"/>}
                          {!Boolean(row.click_thru_url) && <CloseIcon color="error"/>}
                        </Grid>
                        <Grid item sx={{ ml: 1 }}>
                          Click url
                        </Grid>
                      </Grid>
                      <Grid container item xs={12} alignItems="center">
                        <Grid item>
                          {row.lumen_trackers?.some(({ mid }) => Boolean(mid)) && <CheckIcon color="success"/>}
                          {!row.lumen_trackers?.some(({ mid }) => Boolean(mid)) && <CloseIcon color="error"/>}
                        </Grid>
                        <Grid item sx={{ ml: 1 }}>
                          Lumen
                        </Grid>
                      </Grid>
                      <Grid container item xs={12} alignItems="center">
                        <Grid item>
                          {row.impression_trackers?.some(({ url }) => Boolean(url)) && <CheckIcon color="success"/>}
                          {!row.impression_trackers?.some(({ url }) => Boolean(url)) && <CloseIcon color="error"/>}
                        </Grid>
                        <Grid item sx={{ ml: 1 }}>
                          Impression
                        </Grid>
                      </Grid>
                    </Grid>
                  </TableCellBody>
                  <TableCellBody>
                    <IconButton
                      color={'primary'}
                      onClick={() => navigate(`/playlist/creative/${playlist}/${row.id}/tags`)}
                    ><CodeIcon/></IconButton>
                    {/*<DSPCopyTag clickThruURL={row.click_thru_url}*/}
                    {/*            creativeUrl={creativeUrl.toString()}/>*/}
                  </TableCellBody>
                  <TableCellBody align="center">
                    <Grid container justifyContent={'flex-start'} spacing={2}>
                      <Grid item>
                        <PencilIcon style={{ cursor: 'pointer' }}
                                    onClick={() => setCreative(row)}/>
                      </Grid>
                      <Grid item>
                      <span onClick={async () => {
                        if (!window.confirm(`Are you sure you want to remove '${row.name}'?`)) {
                          return;
                        }
                        await onRemove(row.id as number);
                      }} style={{ cursor: 'pointer' }}><img
                        width={20}
                        src={removeButton}
                        alt="Remove player"/></span>
                      </Grid>
                    </Grid>
                  </TableCellBody>
                </TableRow>)
                  ;
              })}
            </TableBody>
          </Table>
          }
          {players.length === 0 && <p>This playlist has no creative yet.</p>}
        </Grid>
      </Grid>}
      {currentPlaylist.format === 'horizontal' &&
        <Typography sx={{ marginTop: '20px' }} variant={'body1'}>Preview is only available for
          vertical video.</Typography>}
      {busy && <LinearProgress color="success"/>}
      {creative && <PlaylistCreativeDialog creativeState={creative} open={Boolean(creative)}
                                           handleClose={() => {
                                             setCreative(null);
                                             dispatchRedux(apiGetPlayer({
                                               publisher: selectedOrganisation,
                                               playlistId: playlist
                                             }));
                                           }}/>}
    </Box>
  );
}
